import React, { useState, useEffect } from 'react';
import { getRequests } from '../utils/requests';
import { getOffers, createOffer } from '../utils/offers';
import { getConversations } from '../utils/messages';
import { getCachedUser } from '../utils/auth';
import { supabase } from '../utils/supabaseClient';
import ChatWindow from './ChatWindow';
import { Skeleton } from './common/Skeleton';
import '../styles/RequestManagement.css';

const RequestManagement = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [activeConversation, setActiveConversation] = useState(null);

  useEffect(() => {
    loadRequests();

    // Subscribe to real-time updates for service requests
    const channel = supabase
      .channel('service_requests_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'service_requests',
          filter: 'status=eq.open',
        },
        (payload) => {
          console.log('Request change detected:', payload);
          loadRequests();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    const { data } = await getRequests({ status: 'open' });
    if (data) {
      // Filter out boosting requests - they have their own dedicated section
      const nonBoostingRequests = data.filter(req => req.service_type !== 'boosting');
      setRequests(nonBoostingRequests);
    }
    setLoading(false);
  };

  const handleMakeOffer = async (requestId, offerData) => {
    const { error } = await createOffer({
      request_id: requestId,
      ...offerData,
    });

    if (error) {
      alert('Failed to create offer: ' + error.message);
      return;
    }

    // Open chat with the request owner
    await openChatForRequest(requestId);
    setShowOfferForm(false);
    await loadRequests();
  };

  const openChatForRequest = async (requestId) => {
    // Get conversation for this request
    const { data: conversations } = await getConversations();
    const conversation = conversations?.find(c => c.request_id === requestId);
    
    if (conversation) {
      setActiveConversation(conversation);
      setShowChat(true);
    } else {
      // Conversation will be created when offer is made
      setShowChat(true);
    }
  };

  if (loading) {
    return (
      <div className="request-management-loading">
        <div className="requests-skeleton-list">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} style={{ 
              background: '#1a1a1a', 
              border: '1px solid #2a2a2a', 
              borderRadius: '8px', 
              padding: '1.5rem', 
              marginBottom: '1rem' 
            }}>
              <Skeleton style={{ width: '40%', height: '20px', marginBottom: '1rem' }} />
              <Skeleton style={{ width: '100%', height: '16px', marginBottom: '0.5rem' }} />
              <Skeleton style={{ width: '80%', height: '16px', marginBottom: '0.5rem' }} />
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <Skeleton style={{ width: '100px', height: '36px', borderRadius: '6px' }} />
                <Skeleton style={{ width: '100px', height: '36px', borderRadius: '6px' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="request-management">
      <div className="request-management-header">
        <h2>Service Requests</h2>
        <p>View open requests and make offers</p>
      </div>

      {showChat && activeConversation && (
        <ChatWindow
          conversation={activeConversation}
          onClose={() => {
            setShowChat(false);
            setActiveConversation(null);
          }}
        />
      )}

      <div className="requests-list">
        {requests.length === 0 ? (
          <div className="no-requests">
            <p>No open requests at the moment.</p>
          </div>
        ) : (
          requests.map(request => (
            <RequestCard
              key={request.id}
              request={request}
              onMakeOffer={() => {
                setSelectedRequest(request);
                setShowOfferForm(true);
              }}
              onViewChat={() => openChatForRequest(request.id)}
            />
          ))
        )}
      </div>

      {showOfferForm && selectedRequest && (
        <OfferForm
          request={selectedRequest}
          onSubmit={(offerData) => handleMakeOffer(selectedRequest.id, offerData)}
          onCancel={() => {
            setShowOfferForm(false);
            setSelectedRequest(null);
          }}
        />
      )}
    </div>
  );
};

const RequestCard = ({ request, onMakeOffer, onViewChat }) => {
  const [offers, setOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const currentUser = getCachedUser();

  useEffect(() => {
    loadOffers();
  }, [request.id]);

  const loadOffers = async () => {
    setLoadingOffers(true);
    const { data } = await getOffers({ request_id: request.id });
    if (data) {
      setOffers(data);
    }
    setLoadingOffers(false);
  };

  const hasMyOffer = offers.some(offer => offer.seller_id === currentUser?.id);

  return (
    <div className="request-card">
      <div className="request-card-header">
        <h3>{request.title}</h3>
        <span className="request-badge">{request.game} • {request.service_type}</span>
      </div>
      {request.description && (
        <p className="request-description">{request.description}</p>
      )}
      {request.metadata && Object.keys(request.metadata).length > 0 && (
        <div className="request-metadata">
          {request.metadata.current_rank && (
            <span>Current: {request.metadata.current_rank}</span>
          )}
          {request.metadata.desired_rank && (
            <span>Desired: {request.metadata.desired_rank}</span>
          )}
        </div>
      )}
      <div className="request-actions">
        {hasMyOffer ? (
          <button onClick={onViewChat} className="view-chat-btn">
            View Chat & Offer
          </button>
        ) : (
          <button onClick={onMakeOffer} className="make-offer-btn">
            Make Offer
          </button>
        )}
        {offers.length > 0 && (
          <span className="offers-count">{offers.length} offer(s)</span>
        )}
      </div>
    </div>
  );
};

const OfferForm = ({ request, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    price: '',
    currency: 'USD',
    message: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.price) {
      alert('Price is required');
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="offer-form-overlay">
      <div className="offer-form">
        <h3>Make Offer on Request</h3>
        <p className="request-title">{request.title}</p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Price *</label>
            <div className="price-input-group">
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
                placeholder="0.00"
              />
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Message (Optional)</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={4}
              placeholder="Add a message with your offer..."
            />
          </div>
          <div className="form-actions">
            <button type="button" onClick={onCancel}>Cancel</button>
            <button type="submit">Send Offer</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestManagement;

